import { useCallback, useContext, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { convertReactSelectObjectToValue } from '../../../../../../utils/reporting/config'
import GenericTable from '../table/GenericTable'
import { ReportingContext } from '../../../../../wrappers/ReportingContext'
import { OrderByColumnsContext } from './OrderByColumnsContext'
import { useEffect } from 'react'
import ReportSelect from '../../../../select/ReportSelect'
import { orderByFunctionOptions } from '../../../../select/StaticSelectObjects'
import OrderByColumnsTableViewOnly from './OrderByColumnsTableViewOnly'
import deepEqual from 'deep-equal'

const OrderByColumns = [
    {
        Header: 'In SELECT?',
        accessor: 'inSelect',
        Cell: ({ row: { original } }) => {
            const { reportConfig } = useContext(ReportingContext)
            const columnsInSelect = useMemo(() => {
                return reportConfig && reportConfig.columns
                    ? reportConfig.columns.map(c => c.sourceColumn)
                    : []
            }, [reportConfig])

            return columnsInSelect.length === 0 ||
                columnsInSelect.includes(original.sourceColumn)
                ? 'Yes'
                : 'No'
        },
    },
    {
        Header: 'Source.Column',
        accessor: 'sourceColumn',
        Cell: ({ value: initialValue, row: { index } }) => {
            const { register } = useFormContext()
            return (
                <>
                    {initialValue && (
                        <span>
                            {initialValue.split('.')[0]}.
                            <b>{initialValue.split('.')[1]}</b>
                        </span>
                    )}
                    <input
                        name={`orderBy[${index}].sourceColumn`}
                        ref={register()}
                        disabled
                        className={`collapsedField`}
                        defaultValue={initialValue}
                    />
                </>
            )
        },
    },
    {
        Header: 'Column Type',
        accessor: 'dataType',
        Cell: ({ value: initialValue, row: { index } }) => {
            const { register } = useFormContext()
            return (
                <>
                    {initialValue}
                    <input
                        name={`orderBy[${index}].dataType`}
                        ref={register()}
                        disabled
                        className={`collapsedField`}
                        defaultValue={initialValue}
                    />
                </>
            )
        },
    },
    {
        Header: () => (
            <span>
                Sort Order<span style={{ color: 'red' }}> *</span>
            </span>
        ),
        accessor: 'direction',
        Cell: ({
            cell,
            value: initialValue,
            row: { original, index },
            column: { id, accessor },
        }) => {
            const { register, control, errors } = useFormContext()
            return (
                <ReportSelect
                    name={`orderBy[${index}].direction`}
                    control={control}
                    rules={{ required: 'Required field' }}
                    options={orderByFunctionOptions}
                    value={convertReactSelectObjectToValue(initialValue)}
                    errors={errors}
                />
            )
        },
    },
]

const OrderByColumnsTable = () => {
    const { formState, setValue } = useFormContext()
    const { fields, append, remove, move } = useFieldArray({
        name: 'orderBy',
    })

    const { resetForm, reportConfig } = useContext(ReportingContext)
    const {
        setRightTableData,
        rightTableHighlights,
        setRightTableHighlights,
        setRightTableFunctions,
    } = useContext(OrderByColumnsContext)
    const [formDirty, setFormDirty] = useState(formState.dirty)

    useEffect(() => {
        setRightTableFunctions(prev => {
            return { ...prev, append: append, remove: remove }
        })
    }, [append, remove, setRightTableFunctions])

    useEffect(() => {
        setRightTableData(fields)
    }, [fields, setRightTableData])

    const [moveMode, setMoveMode] = useState(false)
    useEffect(() => {
        const fieldsWithoutId = fields.map(({ id, ...rest }) => rest)
        const hasMovedColumns = !deepEqual(fieldsWithoutId, reportConfig.orderBy)

        if (moveMode && hasMovedColumns) {
            setValue("orderBy", fieldsWithoutId, { shouldDirty: true })
        }

        const shouldMarkDirty =
            (!moveMode && formState.dirty) ||
            (moveMode && hasMovedColumns)

        setFormDirty(shouldMarkDirty)
    }, [fields, moveMode, formState.dirty, reportConfig.orderBy, setValue])

    const onMoveToggle = useCallback(
        (moveEnabled, setMoveEnabled, setShowConfirmationModal) => {
            if (formDirty) {
                setShowConfirmationModal(true)
            } else {
                setMoveEnabled(prev => !prev)
                setMoveMode(prev => !prev)
            }
        },
        [setMoveMode, formDirty]
    )

    return (
        <>
            {!moveMode ? (
                <GenericTable
                    columns={OrderByColumns}
                    fields={fields}
                    tableHeight={'68vh'}
                    tableTile={'ORDER BY Columns'}
                    viewOnly={false}
                    onHightlight={setRightTableHighlights}
                    highlightedRows={rightTableHighlights}
                    filterable={false}
                    onMoveToggle={onMoveToggle}
                    formProps={{
                        formState,
                        resetForm,
                    }}
                    noDataMessage='No columns selected; the report will not be ordered by any columns.'
                />
            ) : (
                <GenericTable
                    columns={OrderByColumnsTableViewOnly}
                    fields={fields}
                    tableHeight={'68vh'}
                    tableTile={'ORDER BY Columns'}
                    viewOnly={true}
                    onHightlight={setRightTableHighlights}
                    highlightedRows={rightTableHighlights}
                    filterable={false}
                    onMove={move}
                    onMoveToggle={onMoveToggle}
                    formProps={{
                        formState,
                        formDirty,
                        resetForm,
                    }}
                    noDataMessage='No columns selected; the report will not be ordered by any columns.'
                />
            )}
        </>
    )
}

export default OrderByColumnsTable
